import {
  MIN_USD_PRICE,
  TRIMMED_INPUT_REGEX
} from 'constants/constants';
import {
  EPopupSubType,
  EProductPriority,
  ESaleDiscountDisplayType,
  OfferType
} from 'constants/enums';
import { z } from 'zod';

export enum EPopupTriggersAction {
  PURCHASE_COMPLETED = 'purchaseCompleted',
  STORE_REFRESH = 'storeRefresh'
}

export const PRIORITY_NONE_VALUE = '';

export const getPriorityOptions = (): { value: number | string; label: string }[] => [
  { value: PRIORITY_NONE_VALUE, label: 'None' },
  ...Array.from({ length: 5 }, (_, i) => {
    const value = i + 1;
    return {
      value,
      label: value.toString()
    };
  })
];

export const FORM_SCHEMA = z
  .object({
    _id: z.string().optional(),
    active: z.boolean(),
    triggersAction: z.nativeEnum(EPopupTriggersAction),
    triggersFrequency: z.number().min(1),
    triggersOfferExternalIds: z.array(z.string()).optional(),
    name: z
      .string()
      .min(1, 'Name is required')
      .refine((value) => TRIMMED_INPUT_REGEX.test(value), {
        message: 'Name should not be spaces'
      }),
    displayName: z.string().optional(),
    subType: z.nativeEnum(EPopupSubType),
    description: z
      .string()
      .optional()
      .refine((value) => !value || value.length >= 3, {
        message: 'Description length should be at least 3 characters',
        path: ['description']
      }),
    publisherOfferId: z
      .string()
      .min(1, 'External ID is required')
      .refine((value) => TRIMMED_INPUT_REGEX.test(value), {
        message: 'External ID should not be spaces'
      }),
    offerUiId: z.string().min(1, 'Offer Design is required'),
    priority: z.number().min(1).nullable(),
    price: z
      .number({
        required_error: 'Price is required',
        invalid_type_error: 'Price is required'
      })
      .refine((value) => !Number.isNaN(value), { message: 'Price is required' }),
    setAsFree: z.boolean(),
    startOver: z.boolean(),
    segments: z.array(z.string()),
    tags: z.array(z.string()),
    type: z.nativeEnum(OfferType),
    productName: z.string().optional(),
    quantity: z.number(),
    products: z.array(z.any()).optional(),
    salePercentage: z
      .number()
      .min(0, { message: 'Sale amount cannot be negative' })
      .refine((value) => /^\d+(\.\d{1})?$/.test(value.toString()), {
        message: 'Sale amount can have one decimal place at most'
      })
      .optional()
      .default(0),
    salePercentageDisplayType: z
      .string()
      .optional()
      .default(ESaleDiscountDisplayType.PERCENTAGE),
    discount: z
      .number()
      .min(0, { message: 'Price discount cannot be negative' })
      .max(99, { message: 'Price discount cannot be more than 99%' })
      .refine((value) => /^\d+(\.\d{1})?$/.test(value.toString()), {
        message: 'Price discount can have one decimal place at most',
        path: ['discount']
      }),
    priceDiscountType: z.nativeEnum(ESaleDiscountDisplayType),
    coolDownInMinutes: z.number().min(0).optional(),
    playerAvailability: z
      .number()
      .min(1, 'Availability must be at least 1')
      .max(99, 'Availability cannot exceed 99')
      .optional(),
    schedule: z
      .object({
        permanent: z.boolean().optional(),
        timeFrames: z.array(z.any()).optional(),
        interval: z.string().optional()
      })
      .optional(),
    progressBarId: z.string().nullable().optional(),
    progressBarPoints: z.number().nullable().optional(),
    productPriority: z.nativeEnum(EProductPriority).optional().default(EProductPriority.SUB)
  })
  .refine(
    (data) =>
      data.subType === EPopupSubType.DAILY_BONUS ||
      data.setAsFree ||
      data.price >= MIN_USD_PRICE,
    {
      path: ['price'],
      message: `Price must be at least ${MIN_USD_PRICE}`
    }
  )
  .refine(
    (data) =>
      data.subType !== EPopupSubType.POST_PURCHASE ||
      (data.triggersOfferExternalIds?.length ?? 0) > 0,
    {
      path: ['triggersOfferExternalIds'],
      message: 'Offer ID is required'
    }
  );

export type PopupsFormFields = z.infer<typeof FORM_SCHEMA>;

export const DEFAULT_VALUES = {
  active: true,
  name: '',
  displayName: '',
  triggersAction: EPopupTriggersAction.PURCHASE_COMPLETED,
  triggersFrequency: 1,
  triggersOfferExternalIds: [],
  subType: EPopupSubType.DAILY_BONUS,
  description: '',
  publisherOfferId: '',
  offerUiId: '',
  priority: 1,
  price: MIN_USD_PRICE,
  setAsFree: false,
  startOver: true,
  segments: [],
  tags: [],
  type: OfferType.POPUP,
  productName: '',
  quantity: 1,
  products: [],
  salePercentage: 0,
  salePercentageDisplayType: ESaleDiscountDisplayType.PERCENTAGE,
  discount: 0,
  priceDiscountType: ESaleDiscountDisplayType.PERCENTAGE,
  coolDownInMinutes: 1,
  playerAvailability: 1,
  schedule: {
    permanent: true,
    timeFrames: [],
    interval: ''
  },
  progressBarId: null,
  progressBarPoints: null,
  productPriority: EProductPriority.SUB
};
